import React, { useEffect, useRef, useState } from 'react';
import { translations } from '../data/translations';
import { trackVisitor } from '../utils/analytics';

// Components
import About from './sections/About';
import Experience from './sections/Experience';
import Education from './sections/Education';
import Certifications from './sections/Certifications';
import Skills from './sections/Skills';
import Contact from './sections/Contact';
import HTB from './sections/HTB';
import CTFPanel from './CTFPanel';
import LoadingScreen from './LoadingScreen';
import MatrixRain from './MatrixRain';
import TerminalHeader from './TerminalHeader';
import TerminalNav from './TerminalNav';
import MinimizedStandby from './MinimizedStandby';
import TerminalInput from './TerminalInput';
import TerminalOutput from './TerminalOutput';

// Hooks
import useTerminalState from '../hooks/useTerminalState';
import { useCommandProcessor } from '../hooks/useCommandProcessor';

// Styles
import '../styles/terminal.css';

const Terminal = () => {
  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const sessionStartRef = useRef(Date.now());

  const toggleMinimize = () => {
    if (isExiting) return;
    setIsExiting(true);
    // Iris portal animation duration
    setTimeout(() => {
      setIsMinimized(prev => !prev);
      setIsExiting(false);
    }, 560);
  };

  // Use custom hooks for state management
  const {
    isLoading,
    isTransitioning,
    terminalReady,
    showDecrypt,
    handleLoadingComplete,
    input,
    setInput,
    output,
    setOutput,
    addToHistory,
    historyUp,
    historyDown,
    commandHistory,
    activeSection,
    setActiveSection,
    isMatrixMode,
    setIsMatrixMode,
    showCTFPanel,
    setShowCTFPanel,
    currentData,
    language,
    setLanguage
  } = useTerminalState();

  const t = translations[language];

  // Command processor
  const { processCommand } = useCommandProcessor({
    currentData,
    language,
    setLanguage,
    output,
    setOutput,
    setInput,
    addToHistory,
    setActiveSection,
    setIsMatrixMode,
    setShowCTFPanel,
    t
  });

  // Admin session timeout — REMOVED (admin mode deleted in v3.1.1)

  // Welcome message on load
  useEffect(() => {
    if (!isLoading && showDecrypt) {
      const welcomeMessages = [
        {
          type: 'hero',
          logoSrc: currentData.logo,
          tagline: currentData.title
        },
        { type: 'decrypt', content: `${t.welcome} ${currentData.name}`, delay: 1500 },
        { type: 'decrypt', content: `${currentData.title}`, delay: 2300 },
        { type: 'text', content: '' },
        { type: 'decrypt', content: t.helpText, delay: 3100 },
        { type: 'decrypt', content: t.navText, delay: 3700 },
        { type: 'text', content: '' },
        { type: 'decrypt', content: t.tipText, delay: 4300 },
        { type: 'text', content: '' }
      ];
      setOutput(welcomeMessages);
    }
    trackVisitor();
  }, [currentData, t, isLoading, showDecrypt, setOutput]);

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Keyboard handler
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      processCommand(input);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      historyUp();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      historyDown();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Simple autocomplete
      const commands = ['help', 'about', 'experience', 'education', 'skills', 'contact', 'github', 'matrix', 'hack', 'quote', 'web3', 'ctf', 'htb', 'linkedin', 'lang', 'clear'];
      const matchingCommands = commands.filter(cmd => cmd.startsWith(input.toLowerCase()));
      if (matchingCommands.length === 1) {
        setInput(matchingCommands[0]);
      }
    }
  };

  // Render section content
  const renderSection = () => {
    const sectionProps = { data: currentData, language, translations: t };
    switch (activeSection) {
      case 'about': return <About {...sectionProps} />;
      case 'experience': return <Experience {...sectionProps} />;
      case 'education': return <Education {...sectionProps} />;
      case 'certifications': return <Certifications {...sectionProps} />;
      case 'skills': return <Skills {...sectionProps} />;
      case 'contact': return <Contact {...sectionProps} />;
      case 'htb': return <HTB {...sectionProps} />;
      default: return null;
    }
  };

  return (
    <div className="terminal-container">
      {/* Loading Screen */}
      {isLoading && <LoadingScreen onComplete={handleLoadingComplete} />}

      {/* Matrix Rain Effect */}
      <MatrixRain isActive={isMatrixMode} />
      
      {/* Main Terminal */}
      {!isLoading && (
        <div 
          className={`terminal-wrapper ${isTransitioning ? 'terminal-materializing' : ''} ${terminalReady ? 'terminal-ready' : ''}`} 
          style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}
        >
          <TerminalHeader
            logoSrc={currentData.logo}
            isMinimized={isMinimized}
            onToggleMinimize={toggleMinimize}
          />

          <div
            className={`terminal-body${isMinimized ? ' is-minimized' : ''}${isExiting ? ' is-collapsing' : ''}`}
            onClick={() => !isMinimized && inputRef.current?.focus()}
          >
            {isMinimized ? (
              <MinimizedStandby
                logoSrc={currentData.logo}
                name={currentData.name}
                onResume={toggleMinimize}
                sessionStart={sessionStartRef.current}
                commandCount={commandHistory.length}
              />
            ) : (
              <>
                {/* Output */}
                <div className="terminal-output" ref={outputRef}>
                  {output.map((item, index) => (
                    <TerminalOutput key={index} item={item} index={index} />
                  ))}
                </div>

                {/* Input */}
                <TerminalInput
                  ref={inputRef}
                  input={input}
                  setInput={setInput}
                  onKeyDown={handleKeyDown}
                />

                {/* Navigation */}
                <TerminalNav
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  onCTFClick={() => setShowCTFPanel(true)}
                />

                {/* Section Content */}
                {renderSection()}

                {/* Footer */}
                <div className="terminal-footer">
                  <p>© {new Date().getFullYear()} {currentData.name} | {t.footer}</p>
                  <p style={{ marginTop: '10px', fontSize: '12px', opacity: 0.7 }}>{t.footerTip}</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* CTF Panel */}
      {showCTFPanel && (
        <CTFPanel 
          onClose={() => setShowCTFPanel(false)} 
          language={language}
        />
      )}
    </div>
  );
};

export default Terminal;
